/**
 * Design Token Format (DTF) resolver for OneDS Figma-exported tokens.
 *
 * Resolution rules for {token.ref} values:
 *  - PrimeNG Aura namespaces (primary, surface, border, text, …) → kept as-is
 *  - {scale.N}  → Npx  (resolved from OneDS-Primitive.json)
 *  - {ods.core-colours.*} → hex  (resolved from OneDS-Custom.json)
 *  - {colours.*}, {spacing.*}, {radius.*} and any other unknown refs → var(--path-as-kebab)
 *  - bare numbers → Npx
 */

import primitiveTokens from './one-ds-tokens/OneDS-Primitive.json';
import customTokens from './one-ds-tokens/OneDS-Custom.json';

// ---------------------------------------------------------------------------
// Internal token map: flat path → raw $value (built once at module load)
// ---------------------------------------------------------------------------

const internalTokens = new Map<string, string | number>();

function buildTokenMap(obj: unknown, prefix: string): void {
  if (obj === null || typeof obj !== 'object') return;
  const record = obj as Record<string, unknown>;
  if ('$value' in record) {
    internalTokens.set(prefix, record['$value'] as string | number);
    return;
  }
  for (const key of Object.keys(record)) {
    buildTokenMap(record[key], prefix ? `${prefix}.${key}` : key);
  }
}

buildTokenMap(primitiveTokens, '');
buildTokenMap(customTokens, '');

// ---------------------------------------------------------------------------
// PrimeNG Aura token namespaces – keep these as {token.ref} strings so that
// PrimeNG's own design-token resolution system can handle them.
// ---------------------------------------------------------------------------

const PRIMENG_NAMESPACES = new Set([
  'primary',
  'surface',
  'highlight',
  'focus',
  'form',
  'overlay',
  'content',
  'text',
  'navigation',
  'list',
  'mask',
  'anchor',
  'border',
  // Tailwind color palettes that Aura bundles as primitives
  'red',
  'green',
  'blue',
  'orange',
  'purple',
  'yellow',
  'sky',
  'amber',
  'cyan',
  'emerald',
  'fuchsia',
  'gray',
  'indigo',
  'lime',
  'neutral',
  'pink',
  'rose',
  'slate',
  'stone',
  'teal',
  'violet',
  'zinc',
]);

// ---------------------------------------------------------------------------
// Internal reference resolver (recurses through primitive/custom token chains)
// ---------------------------------------------------------------------------

function resolveInternalRef(path: string, visited = new Set<string>()): string | null {
  if (visited.has(path)) return null; // guard against circular refs
  visited.add(path);

  const raw = internalTokens.get(path);
  if (raw == null) return null;
  if (typeof raw === 'number') return `${raw}px`;

  const str = String(raw);
  if (!str.startsWith('{') || !str.endsWith('}')) return str;

  const inner = str.slice(1, -1);
  const ns = inner.split('.')[0];

  // If the chain leads to a PrimeNG token, return it as a token reference
  if (PRIMENG_NAMESPACES.has(ns)) return `{${inner}}`;

  // Try to resolve further
  const resolved = resolveInternalRef(inner, visited);
  if (resolved !== null) return resolved;

  // Unresolvable inner ref → treat as CSS custom property
  return `var(--${inner.replace(/\./g, '-')})`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolves a raw DTF `$value` (string or number) to a PrimeNG-compatible
 * CSS value string.
 *
 * Examples:
 *   resolveValue('{ods.core-colours.brand-light.500}') → '#e20074'
 *   resolveValue('{scale.1-125}')                       → '18px'
 *   resolveValue('{primary.color}')                     → '{primary.color}'
 *   resolveValue('{colours.basic.text}')                → 'var(--colours-basic-text)'
 *   resolveValue('{spacing.component.4}')               → 'var(--spacing-component-4)'
 *   resolveValue(18)                                    → '18px'
 */
export function resolveValue(value: string | number): string {
  if (typeof value === 'number') return `${value}px`;

  return String(value).replace(/\{([^}]+)\}/g, (match: string, path: string) => {
    const ns = path.split('.')[0];

    // Pass PrimeNG tokens through unchanged
    if (PRIMENG_NAMESPACES.has(ns)) return match;

    // Attempt resolution via primitive/custom token maps
    const resolved = resolveInternalRef(path);
    if (resolved !== null) return resolved;

    // Unknown external token (colours.*, spacing.*, radius.*, …) → CSS var
    return `var(--${path.replace(/\./g, '-')})`;
  });
}

/**
 * Extracts the `$value` from a DTF leaf-token object and resolves it.
 *
 * Usage:
 *   t(componentCommonJson.button.gap)  →  '8px'
 *   t(colorSchemeJson.button.primary.background)  →  '{primary.color}'
 */
export function t(dtfToken: { readonly $value: string | number }): string {
  return resolveValue(dtfToken.$value);
}

/**
 * Returns a unitless numeric token value as a plain string (no `px` suffix).
 * Use for font-weight, opacity, z-index, and other dimensionless CSS values.
 *
 * Usage:
 *   tNum(componentCommonJson.stepper.step.title.font.weight)  →  '500'
 */
export function tNum(dtfToken: { readonly $value: number }): string {
  return String(dtfToken.$value);
}

// ---------------------------------------------------------------------------
// tDeep — recursive resolver that preserves JSON structure
// ---------------------------------------------------------------------------

/**
 * Recursively maps a DTF token tree type to a resolved string tree.
 * Leaves ({ $value }) become `string`; $-prefixed metadata keys are stripped;
 * intermediate nodes keep their shape.
 */
export type DeepResolved<T> = T extends { readonly $value: string | number }
  ? string
  : T extends object
    ? { [K in keyof T as K extends `$${string}` ? never : K]: DeepResolved<T[K]> }
    : string;

/**
 * Recursively resolves an entire DTF subtree, preserving the JSON structure.
 * All $-prefixed metadata keys ($type, $description) are stripped.
 * Leaves become resolved CSS strings; intermediate nodes keep their shape.
 *
 * Usage:
 *   const acc = tDeep(componentCommonJson.accordion.header);
 *   acc.hover.background   →  '{content.hover.background}'  (string, not token object)
 */
export function tDeep<T>(token: T): DeepResolved<T> {
  if (token !== null && typeof token === 'object' && '$value' in (token as object)) {
    return resolveValue(
      (token as unknown as { $value: string | number }).$value,
    ) as DeepResolved<T>;
  }
  if (token !== null && typeof token === 'object' && !Array.isArray(token)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(token as object)) {
      if (k.startsWith('$')) continue;
      out[k] = tDeep((token as Record<string, unknown>)[k]);
    }
    return out as DeepResolved<T>;
  }
  if (typeof token === 'string' || typeof token === 'number') {
    return resolveValue(token) as DeepResolved<T>;
  }
  return token as DeepResolved<T>;
}

// ---------------------------------------------------------------------------
// tFlat — flattens a DTF subtree to a camelCase-keyed flat record
// ---------------------------------------------------------------------------

// Joins dot-separated path segments in camelCase: "hover.border.color" → "hoverBorderColor"
type CamelJoin<S extends string> = S extends `${infer H}.${infer T}`
  ? `${H}${Capitalize<CamelJoin<T>>}`
  : S;

// Merges a union of objects into a single intersection type
type UnionToIntersection<U> = (U extends unknown ? (u: U) => void : never) extends (
  u: infer I,
) => void
  ? I
  : never;

// Recursively builds a union of single-key leaf objects from a DTF tree
type FlatDtf<T, Prefix extends string = ''> = T extends { readonly $value: string | number }
  ? { [K in '' extends Prefix ? never : CamelJoin<Prefix>]: string }
  : T extends object
    ? {
        [K in keyof T as K extends `$${string}` ? never : K]: FlatDtf<
          T[K],
          Prefix extends '' ? K & string : `${Prefix}.${K & string}`
        >;
      }[keyof T extends infer U ? (U extends `$${string}` ? never : U) : never]
    : never;

/** The flat camelCase-keyed resolved type for a DTF subtree. */
export type FlatResolved<T> = UnionToIntersection<FlatDtf<T>>;

/**
 * Resolves an entire DTF subtree into a FLAT object with camelCase keys.
 * Nested paths are joined: hover.color → hoverColor, border.radius → borderRadius,
 * active.hover.background → activeHoverBackground, etc.
 *
 * This matches PrimeNG's flat camelCase design token naming convention, so
 * the result can often be passed directly to a PrimeNG component token interface.
 *
 * Usage:
 *   const header = tFlat(componentCommonJson.accordion.header);
 *   // → { padding, color, hoverColor, hoverBackground, activeColor, … }
 *
 *   export const AccordionTelekomDT: AccordionDesignTokens = { header: tFlat(acc.header) };
 *
 * NOTE: Bare numbers resolve WITH a px suffix (e.g. fontWeight → '600px').
 * For dimensionless tokens (fontWeight, opacity), keep using tNum() on the
 * specific leaf and override via spread: { ...tFlat(sub), fontWeight: tNum(leaf) }
 */
export function tFlat<T extends object>(token: T): FlatResolved<T> {
  const result: Record<string, string> = {};
  flattenInto(token, '', result);
  return result as FlatResolved<T>;
}

function flattenInto(node: unknown, prefix: string, out: Record<string, string>): void {
  if (node === null || typeof node !== 'object') return;
  const rec = node as Record<string, unknown>;
  if ('$value' in rec) {
    const key = prefix ? camelJoin(prefix) : 'value';
    out[key] = resolveValue(rec['$value'] as string | number);
    return;
  }
  for (const k of Object.keys(rec)) {
    if (k.startsWith('$')) continue;
    flattenInto(rec[k], prefix ? `${prefix}.${k}` : k, out);
  }
}

function camelJoin(path: string): string {
  return path.replace(/\.([a-z0-9])/gi, (_, c: string) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// tColorPalette — builds a complete PrimeNG primitive color palette from a
// partial DTF color scale by interpolating missing shades
// ---------------------------------------------------------------------------

const STANDARD_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
}

function lerpHex(a: string, b: string, ratio: number): string {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  return rgbToHex(r1 + (r2 - r1) * ratio, g1 + (g2 - g1) * ratio, b1 + (b2 - b1) * ratio);
}

/**
 * Builds a complete PrimeNG primitive color palette (shades 50–950) from a
 * partial DTF color scale. Missing shades are filled by linear interpolation
 * between the nearest present neighbors; edge-missing shades (50, 950) are
 * clamped to the closest available shade.
 *
 * ODS functional palettes omit shades 50, 500, and 950 — this helper fills
 * them automatically without requiring manual fallback values.
 *
 * Usage:
 *   primitive: {
 *     green: tColorPalette(customTokens.ods['core-colours'].functional.green),
 *     red:   tColorPalette(customTokens.ods['core-colours'].functional.red),
 *   }
 */
export function tColorPalette(
  dtfScale: Record<string, { readonly $value: string | number }>,
): Record<string, string> {
  const resolved: Record<number, string> = {};
  for (const [shade, token] of Object.entries(dtfScale)) {
    const n = parseInt(shade, 10);
    if (!isNaN(n)) resolved[n] = t(token);
  }

  const present = Object.keys(resolved)
    .map(Number)
    .sort((a, b) => a - b);
  const result: Record<string, string> = {};

  for (const shade of STANDARD_SHADES) {
    if (resolved[shade] !== undefined) {
      result[String(shade)] = resolved[shade];
      continue;
    }
    const lower = [...present].reverse().find((s) => s < shade);
    const upper = present.find((s) => s > shade);

    if (lower !== undefined && upper !== undefined) {
      result[String(shade)] = lerpHex(
        resolved[lower],
        resolved[upper],
        (shade - lower) / (upper - lower),
      );
    } else {
      // Edge shade with only one neighbor — clamp to nearest
      result[String(shade)] = resolved[lower ?? upper!];
    }
  }

  return result;
}
